/**
 * Cross-Tab Session Sync Module
 *
 * Synchronizes PMW session storage data across browser tabs using
 * BroadcastChannel (primary) with a localStorage event fallback.
 *
 * This ensures that click identifiers (gclid, fbclid, ttclid, etc.),
 * IP/geo data, and other session state are available in all tabs,
 * including tabs opened via right-click → "Open in New Tab".
 *
 * @since 1.57.0
 * @module cross-tab-sync
 */

const CHANNEL_NAME         = "pmw_session_sync"
const LOCALSTORAGE_KEY     = "_pmw_cross_tab_sync"
const SESSION_DATA_PREFIX  = "_pmw_session_data"

/**
 * Message types for cross-tab communication
 */
const MSG = {
	SESSION_REQUEST : "SESSION_REQUEST",
	SESSION_RESPONSE: "SESSION_RESPONSE",
	SESSION_UPDATE  : "SESSION_UPDATE",
}

/**
 * Keys that should NOT be synced across tabs.
 * These are per-tab behavioral flags that represent
 * one-time actions or tab-specific state.
 */
const EXCLUDED_KEYS = new Set([
	"loginEventFired",
	"accountCreatedEventFired",
	"acr_attempted",
	"store_client_ip_in_server_session",
])

/**
 * Guard flag to prevent re-broadcasting data
 * that was received from another tab.
 */
let _isSyncing = false

/**
 * Reference to the BroadcastChannel instance (if supported)
 * @type {BroadcastChannel|null}
 */
let _channel = null

/**
 * Whether the sync module has been initialized
 */
let _initialized = false

/**
 * Check if a session data key should be synced across tabs.
 *
 * @param {string} key - The session data key
 * @return {boolean}
 */
export const isSyncableKey = (key) => {
	return !EXCLUDED_KEYS.has(key)
}

/**
 * Check if we're currently applying synced data (to prevent re-broadcast loops).
 *
 * @return {boolean}
 */
export const isSyncing = () => _isSyncing

/**
 * Filter an object to remove excluded keys.
 *
 * @param {Object} data - The session data object
 * @return {Object} Filtered data with excluded keys removed
 */
const filterSyncableData = (data) => {
	if (!data || typeof data !== "object") return {}

	const filtered = {}
	for (const key of Object.keys(data)) {
		if (isSyncableKey(key)) {
			filtered[key] = data[key]
		}
	}
	return filtered
}

/**
 * Read all session data from sessionStorage, including extension keys.
 *
 * @return {Object} Object with `main` (the default session data) and `extensions` (map of suffix → data)
 */
const readAllSessionData = () => {
	const result = {
		main      : {},
		extensions: {},
	}

	if (!window.sessionStorage) return result

	try {
		// Read main session data
		const mainRaw = window.sessionStorage.getItem(SESSION_DATA_PREFIX)
		if (mainRaw) {
			result.main = JSON.parse(mainRaw)
		}

		// Scan for extension keys (_pmw_session_data_*)
		for (let i = 0; i < window.sessionStorage.length; i++) {
			const storageKey = window.sessionStorage.key(i)
			if (storageKey && storageKey.startsWith(SESSION_DATA_PREFIX + "_")) {
				const suffix = storageKey.substring(SESSION_DATA_PREFIX.length + 1)
				try {
					result.extensions[suffix] = JSON.parse(window.sessionStorage.getItem(storageKey))
				} catch (e) {
					// Skip malformed extension data
				}
			}
		}
	} catch (e) {
		// sessionStorage access failed
	}

	return result
}

/**
 * Merge remote session data into local session storage.
 * Local data takes precedence (local-wins strategy).
 *
 * @param {Object} remoteSnapshot - { main: {}, extensions: {} }
 */
const mergeRemoteData = (remoteSnapshot) => {
	if (!window.sessionStorage || !remoteSnapshot) return

	_isSyncing = true

	try {
		// Merge main session data (local wins)
		if (remoteSnapshot.main && typeof remoteSnapshot.main === "object") {
			const filteredRemote = filterSyncableData(remoteSnapshot.main)
			const localRaw       = window.sessionStorage.getItem(SESSION_DATA_PREFIX)
			const localData      = localRaw ? JSON.parse(localRaw) : {}
			const merged         = {...filteredRemote, ...localData}

			window.sessionStorage.setItem(SESSION_DATA_PREFIX, JSON.stringify(merged))
		}

		// Merge extension keys (local wins)
		if (remoteSnapshot.extensions && typeof remoteSnapshot.extensions === "object") {
			for (const [suffix, remoteData] of Object.entries(remoteSnapshot.extensions)) {
				const extKey   = SESSION_DATA_PREFIX + "_" + suffix
				const localRaw = window.sessionStorage.getItem(extKey)

				if (!localRaw) {
					// No local data for this extension — use remote
					window.sessionStorage.setItem(extKey, JSON.stringify(remoteData))
				}
				// If local data exists, keep it (local wins)
			}
		}
	} catch (e) {
		// Merge failed silently
	}

	_isSyncing = false
}

/**
 * Send a message to other tabs.
 *
 * @param {Object} message - The message object to send
 */
const sendMessage = (message) => {
	try {
		if (_channel) {
			_channel.postMessage(message)
		} else {
			// Fallback: use localStorage event
			// Write → remove pattern ensures the storage event fires every time,
			// even if the message content is the same
			window.localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(message))
			window.localStorage.removeItem(LOCALSTORAGE_KEY)
		}
	} catch (e) {
		// Send failed silently
	}
}

/**
 * Handle an incoming message from another tab.
 *
 * @param {Object} message - The received message
 */
const handleMessage = (message) => {
	if (!message || !message.type) return

	switch (message.type) {

		case MSG.SESSION_REQUEST:
			// Another tab is requesting session data — send ours
			sendMessage({
				type: MSG.SESSION_RESPONSE,
				data: readAllSessionData(),
			})
			break

		case MSG.SESSION_RESPONSE:
			// We received session data from another tab — merge it
			mergeRemoteData(message.data)
			break

		case MSG.SESSION_UPDATE: {
			// A specific key was updated in another tab — apply it locally
			if (!message.key || !isSyncableKey(message.key)) break

			_isSyncing = true

			try {
				if (message.extension) {
					// Extension key update
					const extKey = SESSION_DATA_PREFIX + "_" + message.suffix
					window.sessionStorage.setItem(extKey, JSON.stringify(message.value))
				} else {
					// Main session data key update
					const localRaw  = window.sessionStorage.getItem(SESSION_DATA_PREFIX)
					const localData = localRaw ? JSON.parse(localRaw) : {}
					localData[message.key] = message.value
					window.sessionStorage.setItem(SESSION_DATA_PREFIX, JSON.stringify(localData))
				}
			} catch (e) {
				// Update failed silently
			}

			_isSyncing = false
			break
		}
	}
}

/**
 * Broadcast a session data update to other tabs.
 * Called from pmw.storeData() when a key is written.
 *
 * @param {string} key - The session data key that was updated
 * @param {*} value - The new value
 * @param {boolean} extension - Whether this is an extension key
 * @param {string} suffix - The extension suffix (if extension=true)
 */
export const broadcastUpdate = (key, value, extension = false, suffix = "") => {
	if (_isSyncing) return
	if (!extension && !isSyncableKey(key)) return

	sendMessage({
		type: MSG.SESSION_UPDATE,
		key,
		value,
		extension,
		suffix,
	})
}

/**
 * Request session data from existing tabs.
 * Called when a new tab initializes.
 */
const requestSessionData = () => {
	sendMessage({type: MSG.SESSION_REQUEST})
}

/**
 * Initialize the cross-tab sync module.
 * Sets up either BroadcastChannel or localStorage event listener,
 * then requests session data from existing tabs.
 *
 * @since 1.57.0
 */
export const init = () => {
	if (_initialized) return
	_initialized = true

	try {
		if (typeof BroadcastChannel !== "undefined") {
			// Primary transport: BroadcastChannel
			_channel = new BroadcastChannel(CHANNEL_NAME)
			_channel.onmessage = (event) => handleMessage(event.data)
		} else {
			// Fallback transport: localStorage storage events
			window.addEventListener("storage", (event) => {
				if (event.key !== LOCALSTORAGE_KEY || !event.newValue) return

				try {
					const message = JSON.parse(event.newValue)
					handleMessage(message)
				} catch (e) {
					// Malformed message
				}
			})
		}

		// Request session data from any existing tabs
		requestSessionData()
	} catch (e) {
		// Initialization failed — sync won't be available but PMW still works
	}
}

/**
 * Clean up the cross-tab sync resources.
 * Called on page unload.
 *
 * @since 1.57.0
 */
export const cleanup = () => {
	if (_channel) {
		try {
			_channel.close()
		} catch (e) {
			// Close failed silently
		}
		_channel = null
	}
	_initialized = false
}
